import React, { useEffect, useRef, useState } from "react";
import Matter from "matter-js";

// Define prop types for GoalHitPopup
interface GoalHitPopupProps {
  imagePath: string;
  isVisible: boolean;
  onClick: () => void;
}

const GoalHitPopup: React.FC<GoalHitPopupProps> = ({
  imagePath,
  isVisible,
  onClick,
}) => {
  if (!isVisible) return null;

  const imageName = imagePath.split("/").pop()?.replace(".png", "") ?? "";

  var message = "";
  if (imageName === "tente-outra-vez") {
    message = "";
  } else {
    message = "Parabéns";
  }

  return (
    <div className="goal-hit-popup" onClick={onClick}>
      <div className="goal-hit-content">
        <div className="goal-hit-logo-container"></div>
        <h2 className="goal-hit-message">{message}</h2>
        <div className="goal-hit-image-container">
          <img src={imagePath} alt="Prêmio" className="goal-hit-image" />
        </div>
      </div>
    </div>
  );
};

interface MatterSceneProps {
  goalImages: string[];
  onGoalHit: (imagePath: string) => void;
}

const MatterScene: React.FC<MatterSceneProps> = ({ goalImages, onGoalHit }) => {
  const sceneRef = useRef<HTMLDivElement>(null);
  const engineRef = useRef<Matter.Engine | null>(null);
  const [isSceneReady, setIsSceneReady] = useState(false);
  const [prizeImagePath, setPrizeImagePath] = useState("");
  const [popupVisible, setPopupVisible] = useState(false);

  const canvasRef = useRef({ width: 0, height: 0 });

  const hasGameBallRef = useRef(false);
  const lastLineHeight = useRef(0);
  const hasPrizeBeenShown = useRef(false);

  // Function to create invisible, thin game walls
  const createGameWall = (
    x: number,
    y: number,
    width: number,
    height: number,
  ) => {
    return Matter.Bodies.rectangle(x, y, width, height, {
      isStatic: true,
      render: { visible: false, fillStyle: "transparent" },
    });
  };

  // function to create game ball with image fill
  const createGameBall = (x: number, y: number) => {
    if (!engineRef.current) return null;
    const ballRadius = canvasRef.current.width / 60;

    // Calculate scale to fit image to ball diameter
    const ballDiameter = ballRadius * 2;
    const imageSize = 250;
    const scale = ballDiameter / imageSize;

    return Matter.Bodies.circle(x, y, ballRadius, {
      restitution: 0.8,
      density: 0.01,
      friction: 0.01,
      label: "game-ball",
      render: {
        sprite: {
          texture: "/images/bola.png",
          xScale: scale,
          yScale: scale,
        },
      },
    });
  };

  const restartGame = () => {
    setPopupVisible(false);
    hasGameBallRef.current = false;
    hasPrizeBeenShown.current = false;
    setIsPlaying(false);
  };

  useEffect(() => {
    // module aliases
    var Engine = Matter.Engine,
      Render = Matter.Render,
      Runner = Matter.Runner,
      Bodies = Matter.Bodies,
      Composite = Matter.Composite,
      Body = Matter.Body,
      Events = Matter.Events;

    // create an engine
    var engine = Engine.create();
    engine.gravity.y = 0.7;
    engineRef.current = engine;

    // create a renderer
    const canvasWidth = sceneRef.current?.clientWidth || 800;
    const canvasHeight = sceneRef.current?.clientHeight || 1000;

    // save dimensions for later use
    canvasRef.current = { width: canvasWidth, height: canvasHeight };

    var render = Render.create({
      element: sceneRef.current!,
      engine: engine,
      options: {
        width: canvasWidth,
        height: canvasHeight,
        wireframes: false,
        background: "transparent",
      },
    });

    // create thin, invisible boundary walls for the game area
    const wallThickness = 2;
    const walls = [
      createGameWall(
        canvasRef.current.width / 2,
        0,
        canvasRef.current.width,
        wallThickness,
      ), // top
      createGameWall(
        0,
        canvasRef.current.height / 2,
        wallThickness,
        canvasRef.current.height,
      ), // left
      createGameWall(
        canvasRef.current.width,
        canvasRef.current.height / 2,
        wallThickness,
        canvasRef.current.height,
      ), // right
    ];

    // create goal shapes - rectangles with half circles on top
    const createGoal = (
      x: number,
      y: number,
      goalWidth: number,
      goalBaseHeight: number,
      label: string = "",
    ) => {
      const rectangle = Bodies.rectangle(x, y, goalWidth, goalBaseHeight, {
        isStatic: true,
        isSensor: true,
        label: label,
        render: { fillStyle: /*"#E5B287"*/ "white" },
      });
      const circle = Bodies.circle(x, y - goalBaseHeight / 2, goalWidth / 2, {
        isStatic: true,
        isSensor: true,
        label: label,
        render: { fillStyle: /*"#E5B287"*/ "white" },
      });
      return Body.create({
        isStatic: true,
        parts: [rectangle, circle],
      });
    };

    // constants for goal shapes
    const numGoals = 5;
    const goalGap = canvasWidth / 50;
    const totalgoalGapWidth = (numGoals + 1) * goalGap;
    const goalWidth = (canvasWidth - totalgoalGapWidth) / numGoals;
    const startX = goalGap + goalWidth / 2;
    const goalBaseHeight = canvasHeight / 15 || 80;
    const goalStartY = canvasHeight - goalBaseHeight / 2;
    const goalHeight = goalBaseHeight + goalWidth / 2;

    // create goals
    const goals = Array.from({ length: numGoals }, (_, i) => {
      const x = startX + i * (goalWidth + goalGap);
      const imageName = goalImages[i]?.split("/").pop()?.split(".")[0];
      return createGoal(
        x,
        goalStartY,
        goalWidth,
        goalBaseHeight,
        `goal-${imageName}`,
      );
    });

    // create side wall shapes - trapezoids with base on side of canvas
    const createRoundedWall = (
      x: number,
      y: number,
      width: number,
      height: number,
      slope: number,
      isLeft: boolean = false,
    ) => {
      const trapezoid = Bodies.trapezoid(x, y, width, height, slope, {
        isStatic: true,
        render: { fillStyle: /*"#E5B287"*/ "white" },
      });

      const angle = isLeft ? Math.PI / 2 : -Math.PI / 2;
      Body.rotate(trapezoid, angle);

      const gapAfterRotation = isLeft
        ? trapezoid.vertices[0].x
        : canvasWidth - trapezoid.vertices[0].x;

      Body.setPosition(trapezoid, {
        x: isLeft ? x - gapAfterRotation : x + gapAfterRotation,
        y: y,
      });

      const innerVertex1 = trapezoid.vertices[1];
      const innerVertex2 = trapezoid.vertices[2];
      const circleY = (innerVertex1.y + innerVertex2.y) / 2;
      const circleRadius = Math.abs(innerVertex1.y - innerVertex2.y) / 1.8;

      const circle = Bodies.circle(
        isLeft
          ? innerVertex1.x - circleRadius / 2.12
          : innerVertex1.x + circleRadius / 2.12,
        circleY,
        circleRadius,
        {
          isStatic: true,
          render: { fillStyle: /*"#E5B287"*/ "white" },
        },
      );

      const roundedWall = Composite.create();
      Composite.add(roundedWall, [trapezoid, circle]);

      return roundedWall;
    };

    // constants for wall shapes
    const numWalls = 5;
    const wallGap = canvasHeight / 15;
    const totalWallGapWidth = (numWalls + 1) * wallGap;
    const wallWidth = (canvasHeight * 0.92 - totalWallGapWidth) / numWalls;
    const wallHeight = startX - 3 * goalGap;
    const wallStartY = canvasHeight - goalHeight - wallWidth / 2;

    const leftWalls = Array.from({ length: numWalls }, (_, i) => {
      const x = wallWidth / 2;
      const y = wallStartY - i * (wallWidth + wallGap);
      return createRoundedWall(x, y, wallWidth, wallHeight, 0.6, true);
    });

    const rightWalls = Array.from({ length: numWalls }, (_, i) => {
      const x = canvasWidth - wallWidth / 2;
      const y = wallStartY - i * (wallWidth + wallGap);
      return createRoundedWall(x, y, wallWidth, wallHeight, 0.6, false);
    });

    // create circle obstacles
    const createObstacle = (x: number, y: number, radius: number) => {
      return Bodies.circle(x, y, radius, {
        isStatic: true,
        render: { fillStyle: "white" },
      });
    };

    // constants for circle obstacles
    const obstacleRadius = canvasWidth / 120;

    const numEvenObstacles = 10;
    const evenObstacleStartX = 2.5 * wallHeight;
    const totalEvenObstacleGapWidth =
      canvasWidth -
      2 * evenObstacleStartX -
      2 * obstacleRadius * (numEvenObstacles - 1);
    const evenObstacleGap = totalEvenObstacleGapWidth / (numEvenObstacles - 1);

    const numOddObstacles = 11;
    const oddObstacleStartX = (3 * wallHeight) / 2 + obstacleRadius / 2;
    const totalOddObstacleGapWidth =
      canvasWidth -
      2 * oddObstacleStartX -
      2 * obstacleRadius * (numOddObstacles - 1);
    const oddObstacleGap = totalOddObstacleGapWidth / (numOddObstacles - 1);

    // 4 lines of even line obstacles
    const numEvenLines = 4;
    const evenLineObstacles = Array.from({ length: numEvenLines }, (_, i) => {
      const y =
        canvasHeight - goalHeight - wallWidth / 2 - i * (wallWidth + wallGap);
      if (i === numEvenLines - 1) {
        lastLineHeight.current = y;
      }
      return Array.from({ length: numEvenObstacles }, (_, j) => {
        const x =
          evenObstacleStartX + j * evenObstacleGap + j * 2 * obstacleRadius;
        return createObstacle(x, y, obstacleRadius);
      });
    });

    // 4 lines of odd line obstacles
    const numOddLines = 4;
    const oddLineObstacles = Array.from({ length: numOddLines }, (_, i) => {
      const y =
        canvasHeight -
        goalHeight -
        wallWidth -
        wallGap / 2 -
        i * (wallWidth + wallGap);
      if (i === numOddLines - 1 && numOddLines >= numEvenLines) {
        lastLineHeight.current = y;
      }
      return Array.from({ length: numOddObstacles }, (_, j) => {
        const x =
          oddObstacleStartX + j * oddObstacleGap + j * 2 * obstacleRadius;
        return createObstacle(x, y, obstacleRadius);
      });
    });

    // add all of the bodies to the world
    Composite.add(engine.world, [
      ...walls,
      ...goals,
      ...leftWalls,
      ...rightWalls,
      ...evenLineObstacles.flat(),
      ...oddLineObstacles.flat(),
    ]);

    // run the renderer
    Render.run(render);

    // create runner
    var runner = Runner.create();

    // run the engine
    Runner.run(runner, engine);

    setIsSceneReady(true);

    Events.on(engine, "collisionStart", (event) => {
      if (hasPrizeBeenShown.current) return; // ignore further collisions if already shown

      const pairs = event.pairs;
      pairs.forEach((pair) => {
        const { bodyA, bodyB } = pair;

        const checkGoalHit = (goalLabel: string) => {
          const imageName = goalLabel.slice(5); // removes "goal-"

          // Find the imagePath in goalImages that ends with imageName + ".png"
          const hitImagePath = goalImages.find((path) =>
            path.endsWith(`${imageName}.png`),
          );

          if (hitImagePath) {
            onGoalHit(hitImagePath);

            setPrizeImagePath(hitImagePath);
            setPopupVisible(true);
            hasPrizeBeenShown.current = true;
          }
        };

        if (bodyA.label?.startsWith("goal-") && bodyB.label === "game-ball") {
          checkGoalHit(bodyA.label);
        } else if (
          bodyB.label?.startsWith("goal-") &&
          bodyA.label === "game-ball"
        ) {
          checkGoalHit(bodyB.label);
        }
      });
    });

    // cleanup on component unmount
    return () => {
      Render.stop(render);
      Runner.stop(runner);
      Composite.clear(engine.world, false);
      Engine.clear(engine);
      render.canvas.remove();
      render.textures = {};
      engineRef.current = null;
      setIsSceneReady(false);
    };
  }, [goalImages, onGoalHit]);

  // add a state to track if a game is ongoing
  const [isPlaying, setIsPlaying] = useState(false);

  // update isPlaying when a ball is added or game is restarted
  useEffect(() => {
    if (hasGameBallRef.current) {
      setIsPlaying(true);
    } else {
      setIsPlaying(false);
    }
  }, [isSceneReady, popupVisible]);

  // handle click/touch event to add a ball
  const handleAddBall = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isSceneReady || !engineRef.current || hasGameBallRef.current) return;

    const x = "touches" in e ? e.touches[0].clientX : e.clientX;
    const y = "touches" in e ? e.touches[0].clientY : e.clientY;

    console.log("Adicionando bola em:", x, y);
    console.log("Altura da última linha:", lastLineHeight.current);

    // check if the click/touch is above the first line of obstacles
    if (y > lastLineHeight.current) {
      return;
    }

    hasGameBallRef.current = true;
    setIsPlaying(true);

    const ball = createGameBall(x, y);
    if (ball) {
      Matter.Composite.add(engineRef.current.world, ball);
    }
  };

  return (
    <>
      <div
        ref={sceneRef}
        className={`matter-scene playing-${isPlaying}`}
        onClick={handleAddBall}
        onTouchStart={handleAddBall}
      />
      <GoalHitPopup
        imagePath={prizeImagePath}
        isVisible={popupVisible}
        onClick={restartGame}
      />
    </>
  );
};

export default MatterScene;
